import * as React from "react";
import type { Brand } from "./brand";

/**
 * The few things these shared screens cannot know about the site hosting them.
 *
 * @alight/enrolment-ui renders the same registration journey and host portal on
 * alightcreative.com.au and htbh.org.au (docs/HTBH-MIGRATION.md §8a). Almost
 * nothing differs — the API shape, the tables, the flows are identical — but a
 * document title has to name the right organisation, and the co-branding
 * dropdown in admin → Programs only makes sense on a site that runs programs
 * for OTHER organisations.
 *
 * Everything visual comes from the design tokens the host app sets on <html>,
 * not from here: an htbh page is navy-and-red because apps/htbh's theme.css
 * says so, not because this config asks for it.
 */
export interface EnrolmentUiConfig {
  /** Organisation name for document titles, e.g. "Holy Trinity Baulkham Hills". */
  siteName: string;
  /**
   * Co-branding options offered in admin → Programs. Omit (the htbh case) and
   * the "Registration page branding" control is not rendered at all — the site
   * IS the organisation, so there is nothing to choose between.
   */
  brands?: Record<string, Brand>;
  /**
   * Where the registration journey lives on this site. Alight leads with it at
   * "/enrol"; htbh deliberately does NOT — registration is reached from the
   * event it belongs to, never from the primary nav (§8b), so it sits at
   * "/register". Used by every "enrol now" / "resume" link in these screens.
   */
  registerPath?: string;
  /**
   * Where a visitor browses what's on: Alight's "/programs", htbh's "/events".
   * Linked from the journey's empty and finished states.
   */
  programsPath?: string;
  /** Contact page path — the same on both sites today, hence the default. */
  contactPath?: string;
  /**
   * Whether admin → Submissions may offer "move this registration to another
   * email address".
   *
   * This is NOT a policy switch — both organisations want the tool. It is a
   * capability flag, because the move only WORKS on a site that has somewhere
   * for the parent's confirmation link to land. The move email sends the parent
   * to `${SITE_URL}/move/confirm?token=…` (packages/enrolment/src/handlers/
   * enrolmentMoves.ts), and that route is a per-app file. Offering the button on
   * a site without the page emails a real parent a real link to a 404, which is
   * worse than not offering it at all — so it defaults to OFF and each app opts
   * in once its route exists.
   *
   * TODO (needs a developer — not writable from the Host Studio):
   *   add apps/htbh/src/routes/move.confirm.tsx — a copy of
   *   apps/site/src/routes/move.confirm.tsx, unchanged; the screen itself is
   *   shared (MoveConfirmPage) and already knows which organisation it is on —
   *   then set `canMoveRegistrations: true` in apps/htbh/src/main.tsx.
   *   Until both are done the @ Move button is hidden on htbh.org.au only.
   *   Not urgent while htbh is pre-launch; must be closed before it goes live.
   */
  canMoveRegistrations?: boolean;
}

/** Config with the per-site paths resolved to their defaults. */
export interface ResolvedEnrolmentUiConfig extends EnrolmentUiConfig {
  registerPath: string;
  programsPath: string;
  contactPath: string;
  canMoveRegistrations: boolean;
}

const ConfigContext = React.createContext<ResolvedEnrolmentUiConfig | null>(null);

export function EnrolmentUiProvider({
  config,
  children,
}: {
  config: EnrolmentUiConfig;
  children: React.ReactNode;
}) {
  // Identity-stable so every screen below doesn't re-render when the host app does.
  const value = React.useMemo<ResolvedEnrolmentUiConfig>(
    () => ({
      ...config,
      registerPath: config.registerPath ?? "/enrol",
      programsPath: config.programsPath ?? "/programs",
      contactPath: config.contactPath ?? "/contact",
      // Off unless the app says it has the /move/confirm route — see above.
      canMoveRegistrations: config.canMoveRegistrations ?? false,
    }),
    [
      config.siteName,
      config.brands,
      config.registerPath,
      config.programsPath,
      config.contactPath,
      config.canMoveRegistrations,
    ],
  );
  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useEnrolmentUiConfig(): ResolvedEnrolmentUiConfig {
  const config = React.useContext(ConfigContext);
  if (!config) {
    throw new Error(
      "@alight/enrolment-ui: wrap the router in <EnrolmentUiProvider config={{ siteName }}> — " +
        "the shared screens need to know which organisation they are rendering for.",
    );
  }
  return config;
}

/**
 * Document title for a portal page.
 *
 * Every screen in this package is account, payment or host-portal territory,
 * so they are ALL noindex — hence no canonical URL and no need for the host's
 * SITE_URL. Public pages keep using their own app's useSeo().
 */
export function usePortalSeo(title: string): void {
  const { siteName } = useEnrolmentUiConfig();
  const full = `${title} | ${siteName}`;
  React.useEffect(() => {
    document.title = full;
    upsertMeta("property", "og:title", full);
    upsertMeta("name", "robots", "noindex, nofollow");
    document.head.querySelector('link[rel="canonical"]')?.remove();
  }, [full]);
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
